import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Class,
  CreateClassInput,
  CreateLocationInput,
  Location,
  UpdateClassInput,
  UpdateLocationInput,
} from '@accounting/shared';
import { api } from '../lib/api';

// ── Locations ────────────────────────────────────────────────────────────────

export const locationsKey = (
  companyId: string | null,
  includeInactive = false,
) =>
  [
    'locations',
    companyId ?? 'none',
    includeInactive ? 'all' : 'active',
  ] as const;

export function useLocations(
  companyId: string | null,
  opts: { includeInactive?: boolean } = {},
) {
  return useQuery({
    queryKey: locationsKey(companyId, opts.includeInactive),
    enabled: companyId !== null,
    queryFn: () => {
      const qs = opts.includeInactive ? '?includeInactive=true' : '';
      return api
        .get<{ locations: Location[] }>(
          `/companies/${companyId}/locations${qs}`,
        )
        .then((r) => r.locations);
    },
  });
}

export function useCreateLocation(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLocationInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ location: Location }>(
          `/companies/${companyId}/locations`,
          input,
        )
        .then((r) => r.location);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['locations', companyId ?? 'none'] });
    },
  });
}

export function useUpdateLocation(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateLocationInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ location: Location }>(
          `/companies/${companyId}/locations/${id}`,
          input,
        )
        .then((r) => r.location);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['locations', companyId ?? 'none'] });
    },
  });
}

export function useDeactivateLocation(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api
        .delete<{ location: Location }>(
          `/companies/${companyId}/locations/${id}`,
        )
        .then((r) => r.location);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['locations', companyId ?? 'none'] });
    },
  });
}

// ── Classes ──────────────────────────────────────────────────────────────────

export const classesKey = (
  companyId: string | null,
  includeInactive = false,
) =>
  [
    'classes',
    companyId ?? 'none',
    includeInactive ? 'all' : 'active',
  ] as const;

export function useClasses(
  companyId: string | null,
  opts: { includeInactive?: boolean } = {},
) {
  return useQuery({
    queryKey: classesKey(companyId, opts.includeInactive),
    enabled: companyId !== null,
    queryFn: () => {
      const qs = opts.includeInactive ? '?includeInactive=true' : '';
      return api
        .get<{ classes: Class[] }>(`/companies/${companyId}/classes${qs}`)
        .then((r) => r.classes);
    },
  });
}

export function useCreateClass(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClassInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ class: Class }>(`/companies/${companyId}/classes`, input)
        .then((r) => r.class);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['classes', companyId ?? 'none'] });
    },
  });
}

export function useUpdateClass(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateClassInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ class: Class }>(
          `/companies/${companyId}/classes/${id}`,
          input,
        )
        .then((r) => r.class);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['classes', companyId ?? 'none'] });
    },
  });
}

export function useDeactivateClass(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api
        .delete<{ class: Class }>(`/companies/${companyId}/classes/${id}`)
        .then((r) => r.class);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['classes', companyId ?? 'none'] });
    },
  });
}
